import type { Term } from "@onestore/api/types"
import type { HTTP } from "@onestore/onestore-store-common"
import { HTTP_STATUS } from "@onestore/onestore-store-common/http"
import type { Draft } from "immer"
import { produce } from "immer"
import { every } from "lodash"
import { CheckoutActions } from "~/store/checkout/constants"
import type { CheckoutTermsAction } from "~/store/checkout-terms/actions"
import { CheckoutTermsActionType } from "~/store/checkout-terms/constants"

export type CheckoutTermsState = {
  readonly terms: Term[]
  readonly termsValid?: boolean
  readonly termTabRequiredId?: number
  readonly activeTermTabId: number
  readonly termsRequest: HTTP.Status
}

export const initialState: CheckoutTermsState = {
  // terms required to be accepted for the services in the current basket
  terms: [],
  termsValid: undefined,
  termTabRequiredId: undefined,
  activeTermTabId: 0,
  termsRequest: HTTP_STATUS.NONE,
}

export default function reducer(
  state: CheckoutTermsState = initialState,
  action: CheckoutTermsAction
): CheckoutTermsState {
  return produce<CheckoutTermsState, CheckoutTermsState>(
    state,
    (nextState: Draft<CheckoutTermsState>) => {
      switch (action.type) {
        case CheckoutActions.CHECKOUT_RESET:
          nextState.termsValid = undefined
          nextState.termsRequest = HTTP_STATUS.NONE

          return
        case CheckoutTermsActionType.TERMS_ADD_TERM_TAB_REQUIRED_ID:
          nextState.termTabRequiredId = action.termTabRequiredId
          nextState.termsValid = undefined

          return

        case CheckoutTermsActionType.TERMS_ACTIVE_TERM_TAB_ID:
          nextState.activeTermTabId = action.activeTermTabId

          return
        case CheckoutTermsActionType.TERMS_PENDING:
          nextState.termsRequest = HTTP_STATUS.CONTINUE

          return
        case CheckoutTermsActionType.TERMS_VALIDATION:
          nextState.termsValid = action.value

          return

        case CheckoutTermsActionType.TERMS_FAILURE:
          nextState.termsRequest = action.response_status_code
          nextState.terms = []

          return
        case CheckoutTermsActionType.TERMS_SUCCESS:
          nextState.termsRequest = action.response_status_code

          const activeTabId = state.activeTermTabId
          const checkedTerms = state.terms
            .filter((term: Term) => term.checked[activeTabId])
            .reduce(
              (reduction, term) => {
                reduction[term.type][term.id] = term.checked[activeTabId]

                return reduction
              },
              { internal: {}, external: {} }
            )

          nextState.terms = action.result.map((term: Term): Term => {
            if (!checkedTerms[term.type][term.id]) {
              return term
            }

            return {
              ...term,
              checked: [
                checkedTerms[term.type][term.id],
                checkedTerms[term.type][term.id],
              ],
            }
          })

          return
        case CheckoutTermsActionType.TERMS_TOGGLE_ALL_TERMS:
          const allTermsChecked = every(
            state.terms,
            (term) => term.checked[state.activeTermTabId]
          )

          nextState.terms = state.terms.map((term: Term) => {
            return {
              ...term,
              checked: term.checked.map((checked: boolean, index: number) =>
                index === state.activeTermTabId ? !allTermsChecked : checked
              ),
            }
          })

          nextState.termsValid = undefined

          return
        case CheckoutTermsActionType.TERMS_TOGGLE_TERMS:
          nextState.terms = state.terms.map((term: Term) => {
            const isToogledTerm = action.terms.some(
              (actionTerm) =>
                actionTerm.id === term.id && actionTerm.type === term.type
            )

            if (!isToogledTerm) {
              return term
            }

            return {
              ...term,
              checked: term.checked.map((checked: boolean, index: number) =>
                index === state.activeTermTabId ? !checked : checked
              ),
            }
          })
          nextState.termsValid = undefined

          return
      }
    }
  )
}
